import { useEffect, useRef } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import {
  useIncidentWorkflowActionsQuery,
  useIncidentWorkflowByIdQuery,
} from '@/graphql/generated/hooks'
import type {
  IncidentQuery,
  IncidentWorkflowActionsQuery,
  IncidentWorkflowByIdQuery,
} from '@/graphql/generated/operations'
import {
  IncidentStatus,
  IncidentWorkflowStatus,
} from '@/graphql/generated/schemas'
import { useAppDispatch } from '@/redux/hooks'
import { openWorkflowDrawer } from '@/redux/ui/uiSlice'

import { IncidentDataType } from '../types/types'
import {
  getLastWorkflowAction,
  isTerminalWorkflowAction,
} from '../utils/workflowActions'

interface UseIncidentWorkflowIProps {
  incident: IncidentDataType
}

export type AssignedWorkflow = NonNullable<
  IncidentWorkflowByIdQuery['incidentWorkflow']
>
export type PendingWorkflow = NonNullable<
  NonNullable<IncidentQuery['incident']>['pendingWorkflow']
>
export type WorkflowStep = AssignedWorkflow['steps'][number]
export type WorkflowAction =
  IncidentWorkflowActionsQuery['incidentWorkflowActions'][number]

type UseIncidentWorkflowResult = {
  hasWorkflows: boolean
  isWorkflowFeatureEnabled: boolean
  workflowStatus: IncidentWorkflowStatus | null
  pendingWorkflow: PendingWorkflow | null
  pendingWorkflowFull: AssignedWorkflow | null
  assignedWorkflow: AssignedWorkflow | null
  workflowActions: WorkflowAction[]
  currentStepId: string | null
  isLoadingWorkflow: boolean
}

export const useIncidentWorkflow = ({
  incident,
}: UseIncidentWorkflowIProps): UseIncidentWorkflowResult => {
  const dispatch = useAppDispatch()
  const isEnabled = useFeatureFlag(FeatureFlag.incidentResponseWorkflow)

  const workflowStatus = incident?.workflowStatus ?? null
  const pendingWorkflow = incident?.pendingWorkflow ?? null
  const workflowId = incident?.workflowId ?? null
  const pendingVersionId = pendingWorkflow?.versionId ?? null
  const versionId = workflowId ?? pendingVersionId

  const hasWorkflows =
    !!isEnabled &&
    ((workflowStatus != null &&
      workflowStatus !== IncidentWorkflowStatus.NotAssigned) ||
      pendingWorkflow !== null)

  // Fetch the workflow definition (steps, prompts, edges) — shared between pending preview
  // and in-progress/complete rendering since both states reference the same version.
  const { data: workflowData, loading: isLoadingWorkflowDef } =
    useIncidentWorkflowByIdQuery({
      fetchPolicy: 'cache-and-network',
      skip: !versionId || !isEnabled,
      variables: {
        versionId: versionId ?? '',
      },
    })

  // Fetch the action history (which steps were taken and what answers were given).
  const { data: workflowActionsData, loading: isLoadingActions } =
    useIncidentWorkflowActionsQuery({
      fetchPolicy: 'cache-and-network',
      skip: !workflowId || !isEnabled || !incident?.id,
      variables: {
        incidentId: incident?.id ?? '',
      },
    })

  const workflow = workflowData?.incidentWorkflow ?? null
  const workflowActions = workflowActionsData?.incidentWorkflowActions ?? []

  // Derive the current step from the action history rather than the incident
  // query, so the workflow UI only depends on a single data source.
  const lastAction = getLastWorkflowAction(workflowActions)
  const currentStepId = isTerminalWorkflowAction(lastAction)
    ? null
    : lastAction?.targetNodeId ?? workflow?.startingStep ?? null

  const isLoadingWorkflow = isLoadingWorkflowDef || isLoadingActions

  // Auto-open the WorkflowDrawer only when the SAME incident transitions to
  // IN_PROGRESS (i.e. the operator just pressed acknowledge). Navigating to a
  // different incident that is already IN_PROGRESS must not re-open the drawer.
  const prevIncidentIdRef = useRef(incident?.id)
  const prevStatusRef = useRef(incident?.status)
  useEffect(() => {
    const prevIncidentId = prevIncidentIdRef.current
    const prevStatus = prevStatusRef.current
    prevIncidentIdRef.current = incident?.id
    prevStatusRef.current = incident?.status

    if (
      incident?.id === prevIncidentId &&
      incident?.status === IncidentStatus.InProgress &&
      prevStatus !== IncidentStatus.InProgress &&
      hasWorkflows
    ) {
      dispatch(openWorkflowDrawer())
    }
  }, [incident?.id, incident?.status, hasWorkflows])

  return {
    hasWorkflows,
    isWorkflowFeatureEnabled: !!isEnabled,
    workflowStatus,
    pendingWorkflow,
    pendingWorkflowFull: workflow,
    assignedWorkflow: workflow,
    workflowActions,
    currentStepId,
    isLoadingWorkflow,
  }
}
